/**
 * The API client.
 *
 * Same-origin: one container serves the SPA and the API, and Vite proxies /v1 in dev,
 * so there is no base URL to configure and no CORS to get wrong.
 */

export class ApiError extends Error {
  readonly status: number;

  constructor(status: number, message: string) {
    super(message);
    this.name = 'ApiError';
    this.status = status;
  }

  /** Not signed in. The caller should send the viewer to sign in, not retry. */
  get isUnauthorized(): boolean {
    return this.status === 401;
  }

  /** Signed in, but not permitted. Retrying will never help. */
  get isForbidden(): boolean {
    return this.status === 403;
  }
}

/**
 * Dev-only identity switches.
 *
 * Automated test auth reads who you are from headers, and this is how the browser sets
 * them. Stored per-tab so two tabs can hold different sessions.
 *
 * import.meta.env.DEV is compile-time, so none of this exists in a production bundle.
 */
export interface DevIdentity {
  user?: 'admin' | 'partner' | 'partner-admin';
  partner?: string;
  impersonate?: string;
}

const DEV_KEY = 'sparkery.dev-identity';

export function readDevIdentity(): DevIdentity {
  if (!import.meta.env.DEV) return {};
  try {
    const raw = sessionStorage.getItem(DEV_KEY);
    return raw ? (JSON.parse(raw) as DevIdentity) : {};
  } catch {
    return {};
  }
}

export function writeDevIdentity(identity: DevIdentity): void {
  if (!import.meta.env.DEV) return;
  try {
    sessionStorage.setItem(DEV_KEY, JSON.stringify(identity));
  } catch {
    // A private window with storage blocked is not a reason to fail.
  }
}

/**
 * How a request gets its bearer token.
 *
 * A module-level getter, as `app/insights/src/app/api/auth.ts` does it: the fetch
 * helpers are plain functions used from queries and mutations all over the app, and
 * threading an auth object through every one of them would touch every call site. The
 * auth provider installs the getter once it has a session.
 *
 * Held as a FUNCTION rather than a token string on purpose — AuthKit rotates the access
 * token, so a cached string stops working at the first refresh.
 */
export type AuthTokenGetter = () => Promise<string | undefined>;

let getAuthToken: AuthTokenGetter | null = null;

export function setAuthTokenGetter(getter: AuthTokenGetter | null): void {
  getAuthToken = getter;
}

async function authHeaders(): Promise<Record<string, string>> {
  if (!getAuthToken) return {};
  const token = await getAuthToken();
  return token ? { authorization: `Bearer ${token}` } : {};
}

function devHeaders(): Record<string, string> {
  if (!import.meta.env.DEV) return {};

  const identity = readDevIdentity();
  const headers: Record<string, string> = {};
  if (identity.user) headers['x-dev-user'] = identity.user;
  if (identity.partner) headers['x-dev-partner'] = identity.partner;
  if (identity.impersonate) headers['x-dev-impersonate'] = identity.impersonate;
  return headers;
}

export async function apiGet<T>(path: string, signal?: AbortSignal): Promise<T> {
  const response = await fetch(`/v1${path}`, {
    headers: { accept: 'application/json', ...(await authHeaders()), ...devHeaders() },
    credentials: 'same-origin',
    ...(signal ? { signal } : {}),
  });

  if (!response.ok) {
    throw new ApiError(response.status, await messageFor(response));
  }

  return (await response.json()) as T;
}

/**
 * A write.
 *
 * The server's message is surfaced verbatim on failure — a 409 saying which partner
 * already holds a code is the whole point of the response, and swallowing it for a
 * generic "something went wrong" would leave the admin with no way forward.
 */
export async function apiPost<T>(path: string, body: unknown): Promise<T> {
  const response = await fetch(`/v1${path}`, {
    method: 'POST',
    headers: {
      accept: 'application/json',
      'content-type': 'application/json',
      ...(await authHeaders()),
      ...devHeaders(),
    },
    credentials: 'same-origin',
    body: JSON.stringify(body),
  });

  if (!response.ok) {
    throw new ApiError(response.status, await messageFor(response));
  }

  return (await response.json()) as T;
}

/**
 * A partial change.
 *
 * PATCH rather than PUT because every one of these bodies is "the fields I edited" —
 * a PUT would mean the client had to send back values it never showed anybody, which
 * is how a form quietly reverts a field somebody else changed.
 */
export async function apiPatch<T>(path: string, body: unknown): Promise<T> {
  const response = await fetch(`/v1${path}`, {
    method: 'PATCH',
    headers: {
      accept: 'application/json',
      'content-type': 'application/json',
      ...(await authHeaders()),
      ...devHeaders(),
    },
    credentials: 'same-origin',
    body: JSON.stringify(body),
  });

  if (!response.ok) {
    throw new ApiError(response.status, await messageFor(response));
  }

  return (await response.json()) as T;
}

/**
 * A delete.
 *
 * Separate from `apiPost` rather than a `method` parameter, so a call site reads as
 * what it does — and so the absence of a body is structural rather than a convention.
 */
export async function apiDelete<T>(path: string): Promise<T> {
  const response = await fetch(`/v1${path}`, {
    method: 'DELETE',
    headers: { accept: 'application/json', ...(await authHeaders()), ...devHeaders() },
    credentials: 'same-origin',
  });

  if (!response.ok) {
    throw new ApiError(response.status, await messageFor(response));
  }

  return (await response.json()) as T;
}

async function messageFor(response: Response): Promise<string> {
  try {
    const body = (await response.json()) as { message?: string; error?: string };
    return body.message ?? body.error ?? `Request failed (${response.status})`;
  } catch {
    return `Request failed (${response.status})`;
  }
}
